import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, ArrowLeft, Check, MonitorSmartphone, School, CreditCard, Smartphone, QrCode } from 'lucide-react';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './Firebase';
import './Postuler.css';
import logo from './safi1.jpeg';
import safi10 from './safi10.webp';
import waveQR from './wave-qr.jpeg';

const FRAIS_INSCRIPTION = 50000; // Même prix pour tous les niveaux
const FRAIS = { A1: 30000, A2: 30000, B1: 30000, B2: 30000, C1: 30000, C2: 30000 };
const NIVEAUX = ['A1','A2','B1','B2','C1','C2'];
const WAVE_NUMBER = '70 963 34 89';

function validatePassword(pwd) {
  return {
    length:  pwd.length >= 8,
    upper:   /[A-Z]/.test(pwd),
    lower:   /[a-z]/.test(pwd),
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
  };
}

function SuccessPage({ nom }) {
  const navigate = useNavigate();
  const [count, setCount] = React.useState(10);
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCount(prev => {
        if (prev <= 1) { clearInterval(interval); navigate('/connexion'); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [navigate]);

  return (
    <div className="success-page">
      <div className="success-page__card">
        <Link to="/"><img src={logo} alt="SAFI Academia" className="success-page__logo" /></Link>
        <div className="success-page__icon"><Check size={32} strokeWidth={3} /></div>
        <h2 className="success-page__title">Merci, {nom?.split(' ')[0]} ! 🎉</h2>
        <p style={{color:'#555',marginBottom:'0.5rem',fontSize:'0.95rem',textAlign:'center'}}>
          Votre dossier a bien été enregistré et votre paiement Wave est en cours de vérification.
        </p>
        <div style={{background:'rgba(201,168,76,0.1)',border:'1.5px solid rgba(201,168,76,0.3)',borderRadius:10,padding:'1rem',margin:'0.75rem 0',textAlign:'left'}}>
          <p style={{fontSize:'0.88rem',color:'#8a6a1e',fontWeight:600,marginBottom:'0.4rem'}}>⏳ Prochaine étape</p>
          <p style={{fontSize:'0.85rem',color:'#555',lineHeight:1.6}}>
            Notre équipe va vérifier votre paiement. Une fois confirmé, votre accès à l'espace étudiant sera activé.
            Vous pourrez alors vous connecter avec votre email et mot de passe.
          </p>
        </div>
        <p style={{color:'#888',fontSize:'0.82rem',textAlign:'center'}}>
          Conservez bien vos identifiants de connexion.
        </p>
        <button className="postuler__btn" style={{marginTop:'1rem'}} onClick={() => navigate('/')}>
          Retour à l'accueil →
        </button>
      </div>
    </div>
  );
}

export default function Postuler() {
  const navigate = useNavigate();
  const [step, setStep]     = useState(1);
  const [done, setDone]     = useState(false);
  const [loading, setLoading] = useState(false);
  const [firebaseError, setFirebaseError] = useState('');
  const [showPwd, setShowPwd]         = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [paidConfirmed, setPaidConfirmed] = useState(false);

  const [form, setForm] = useState({
    nom: '', prenom: '', email: '', dateNaissance: '',
    nationalite: '', niveau: 'A1', presentiel: false,
    password: '', confirm: '',
  });
  const [errors, setErrors] = useState({});

  const set = field => e => setForm(p => ({...p, [field]: e.target.value}));
  const pwdRules = validatePassword(form.password);

  // ── VALIDATION ÉTAPE 1 ──────────────────────────────────────────────────────
  function validateStep1() {
    const e = {};
    if (!form.prenom.trim())     e.prenom = 'Prénom requis';
    if (!form.nom.trim())        e.nom    = 'Nom requis';
    if (!form.email.includes('@')) e.email = 'Email invalide';
    if (!form.dateNaissance)     e.dateNaissance = 'Date de naissance requise';
    if (!form.nationalite)       e.nationalite   = 'Nationalité requise';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  // ── VALIDATION ÉTAPE 2 ──────────────────────────────────────────────────────
  function validateStep2() {
    const e = {};
    if (!Object.values(pwdRules).every(Boolean)) e.password = 'Mot de passe trop faible';
    if (form.password !== form.confirm) e.confirm = 'Les mots de passe ne correspondent pas';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  // ── SOUMISSION ──────────────────────────────────────────────────────────────
  async function handleSubmit() {
    if (!paidConfirmed) return;
    setLoading(true);
    setFirebaseError('');
    try {
      const nomComplet = `${form.prenom.trim()} ${form.nom.trim()}`;
      const cred = await createUserWithEmailAndPassword(auth, form.email, form.password);
      await updateProfile(cred.user, { displayName: nomComplet });

      // Créer le document étudiant — inactif en attente de confirmation
      await setDoc(doc(db, 'etudiants', cred.user.uid), {
        nom:           nomComplet,
        email:         form.email,
        dateNaissance: form.dateNaissance,
        pays:          form.nationalite,
        niveau:        form.niveau,
        presentiel:    form.presentiel,
        role:          'etudiant',
        actif:         false, // inactif jusqu'à confirmation IT Manager
        paiementStatut: 'en attente',
        createdAt:     serverTimestamp(),
      });

      // Enregistrer le paiement en attente
      const payId = `${cred.user.uid}_inscription_${Date.now()}`;
      await setDoc(doc(db, 'paiements', payId), {
        etudiantId:    cred.user.uid,
        etudiantNom:   nomComplet,
        etudiantEmail: form.email,
        niveau:        form.niveau,
        montant:       FRAIS_INSCRIPTION,
        mode:          'Wave',
        type:          'inscription',
        statut:        'en attente',
        mois:          new Date().toISOString().slice(0, 7),
        createdAt:     serverTimestamp(),
      });

      setDone(true);
    } catch (err) {
      const msgs = {
        'auth/email-already-in-use': 'Cet email est déjà utilisé.',
        'auth/weak-password':        'Mot de passe trop faible.',
        'auth/invalid-email':        'Email invalide.',
      };
      setFirebaseError(msgs[err.code] || 'Erreur. Réessayez.');
    }
    setLoading(false);
  }

  if (done) return <SuccessPage nom={`${form.prenom} ${form.nom}`} />;

  const montant = FRAIS_INSCRIPTION; // 50 000 FCFA pour tous les niveaux

  return (
    <div className="postuler">
      <div className="postuler__left">
        <img src={safi10} alt="" className="postuler__bg" />
        <div className="postuler__left-overlay" />
        <div className="postuler__left-content">
          <Link to="/"><img src={logo} alt="SAFI Academia" className="postuler__logo" /></Link>
          <h1 className="postuler__left-title">Rejoignez<br/>SAFI Academia</h1>
          <p className="postuler__left-sub">Votre passeport vers une maîtrise complète de l'anglais.</p>
          {/* Étapes */}
          <div className="postuler__steps">
            {['Informations','Sécurité','Paiement'].map((s, i) => (
              <div key={i} className={`postuler__step ${step > i+1 ? 'done' : step === i+1 ? 'active' : ''}`}>
                <span className="postuler__step-num">{step > i+1 ? '✓' : i+1}</span>
                <span>{s}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="postuler__right">
        <div className="postuler__form-wrap">
          <Link to="/" className="postuler__back">← Retour à l'accueil</Link>

          {/* ── ÉTAPE 1 — Infos ── */}
          {step === 1 && (
            <>
              <h2 className="postuler__title">Vos informations</h2>
              <p className="postuler__sub">Remplissez vos informations personnelles.</p>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem'}}>
                {[['prenom','Prénom'],['nom','Nom']].map(([field,label]) => (
                  <div key={field} className="postuler__field">
                    <label>{label}</label>
                    <input type="text" value={form[field]} onChange={set(field)}
                      className={errors[field]?'error':''} placeholder={label}/>
                    {errors[field] && <span className="postuler__error">{errors[field]}</span>}
                  </div>
                ))}
              </div>

              {[['email','Email','email'],['dateNaissance','Date de naissance','date']].map(([field,label,type]) => (
                <div key={field} className="postuler__field">
                  <label>{label}</label>
                  <input type={type} value={form[field]} onChange={set(field)}
                    className={errors[field]?'error':''} placeholder={label}/>
                  {errors[field] && <span className="postuler__error">{errors[field]}</span>}
                </div>
              ))}

              <div className="postuler__field">
                <label>Pays / Nationalité</label>
                <select value={form.nationalite} onChange={set('nationalite')} className={errors.nationalite?'error':''}>
                  <option value="">Sélectionner...</option>
                  {['Sénégal','Gabon','Côte d\'Ivoire','Mali','Cameroun','Togo','Bénin','Guinée','France','Autre'].map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                {errors.nationalite && <span className="postuler__error">{errors.nationalite}</span>}
              </div>

              <button className="postuler__btn" onClick={() => { if(validateStep1()) setStep(2); }}>
                Continuer <ArrowRight size={16}/>
              </button>
            </>
          )}

          {/* ── ÉTAPE 2 — Sécurité ── */}
          {step === 2 && (
            <>
              <h2 className="postuler__title">Sécurisez votre compte</h2>
              <p className="postuler__sub">Choisissez un mot de passe pour votre espace étudiant.</p>

              <div className="postuler__field">
                <label>Mot de passe</label>
                <div className="postuler__input-wrap">
                  <input type={showPwd?'text':'password'} value={form.password} onChange={set('password')}
                    className={errors.password?'error':''} placeholder="••••••••"/>
                  <button type="button" className="postuler__eye" onClick={() => setShowPwd(!showPwd)}>
                    {showPwd ? <EyeOff size={18}/> : <Eye size={18}/>}
                  </button>
                </div>
                <div className="postuler__pwd-rules">
                  {[['length','8 car. min'],['upper','1 majuscule'],['lower','1 minuscule'],['special','1 spécial']].map(([k,l]) => (
                    <span key={k} className={pwdRules[k]?'ok':''}>{pwdRules[k]?'✓':'○'} {l}</span>
                  ))}
                </div>
              </div>

              <div className="postuler__field">
                <label>Confirmer le mot de passe</label>
                <div className="postuler__input-wrap">
                  <input type={showConfirm?'text':'password'} value={form.confirm} onChange={set('confirm')}
                    className={errors.confirm?'error':''} placeholder="••••••••"/>
                  <button type="button" className="postuler__eye" onClick={() => setShowConfirm(!showConfirm)}>
                    {showConfirm ? <EyeOff size={18}/> : <Eye size={18}/>}
                  </button>
                </div>
                {errors.confirm && <span className="postuler__error">{errors.confirm}</span>}
              </div>

              <div style={{display:'flex',gap:'0.75rem',marginTop:'1.25rem'}}>
                <button className="postuler__btn postuler__btn--secondary" onClick={() => setStep(1)}>
                  <ArrowLeft size={16}/> Retour
                </button>
                <button className="postuler__btn" onClick={() => { if(validateStep2()) setStep(3); }}>
                  Continuer <ArrowRight size={16}/>
                </button>
              </div>
            </>
          )}

          {/* ── ÉTAPE 3 — Paiement Wave ── */}
          {step === 3 && (
            <>
              <h2 className="postuler__title">Paiement Wave</h2>
              <p className="postuler__sub">Payez vos frais d'inscription pour activer votre compte.</p>

              {/* Récapitulatif */}
              <div className="postuler__recap">
                <div className="postuler__recap-row">
                  <span>Nom</span><strong>{form.prenom} {form.nom}</strong>
                </div>
                <div className="postuler__recap-row">
                  <span>Email</span><strong>{form.email}</strong>
                </div>
                <div className="postuler__recap-row postuler__recap-total">
                  <span>Frais d'inscription</span>
                  <strong>50 000 FCFA</strong>
                </div>
              </div>

              {/* QR Code Wave */}
              <div className="postuler__wave-box">
                <div className="postuler__wave-header">
                  <Smartphone size={20} color="#00B4FF"/>
                  <span>Payer <strong>50 000 FCFA</strong> avec Wave</span>
                </div>

                <div className="postuler__wave-qr">
                  <img
                    src={waveQR}
                    alt="QR Code Wave SAFI Academia"
                    className="postuler__wave-qr__img"
                  />
                  <p className="postuler__wave-qr__hint">Scannez avec l'app Wave pour payer</p>
                </div>

                <label className="postuler__wave-confirm">
                  <input type="checkbox" checked={paidConfirmed}
                    onChange={e => setPaidConfirmed(e.target.checked)}/>
                  <span>✅ J'ai effectué le paiement de <strong>50 000 FCFA</strong> via Wave</span>
                </label>
              </div>

              {firebaseError && <div className="postuler__firebase-error">{firebaseError}</div>}

              <div style={{display:'flex',gap:'0.75rem',marginTop:'1.25rem'}}>
                <button className="postuler__btn postuler__btn--secondary" onClick={() => setStep(2)}>
                  <ArrowLeft size={16}/> Retour
                </button>
                <button className="postuler__btn" onClick={handleSubmit}
                  disabled={loading || !paidConfirmed}
                  style={{opacity: paidConfirmed ? 1 : 0.5}}>
                  {loading ? <span className="postuler__spinner"/> : <><CreditCard size={16}/> Activer mon compte</>}
                </button>
              </div>
            </>
          )}

          <p className="postuler__login">
            Déjà inscrit ? <Link to="/connexion">Se connecter</Link>
          </p>
        </div>
      </div>
    </div>
  );
}